import { StoreConfiguration } from "@/common/models/StoreConfiguration";
import { CreateStoreCoordinates } from "@/stores/pages/models/CreateStoreCoordinates";
import { StorePaymentConfiguration, StorePaymentConfigurationType } from "@/common/models/StorePaymentConfiguration";
import { PosConfiguration, PosConfigurationType } from "@/common/models/PosConfiguration";
import { MetaData } from "@/common/models/MetaData";
import { AdminStore } from "@/common/models/AdminStore";
import { ClickAndCollectConfiguration } from "@/common/models/ClickAndCollectConfiguration";
import { CalendarConfiguration } from "@/common/models/CalendarConfiguration";

export class CreateAdminStoreData {
  code: string | null;
  name: string | null;
  address: string | null;
  email: string | null;
  telephone: string | null;
  location: CreateStoreCoordinates;
  timeZone: string | null;
  vatNumber: string | null;
  active: boolean;
  configuration: StoreConfiguration;
  metaData: MetaData | null;

  constructor() {
    this.code = null;
    this.name = null;
    this.address = null;
    this.email = null;
    this.telephone = null;
    this.location = new CreateStoreCoordinates();
    this.timeZone = null;
    this.vatNumber = null;
    this.active = true;
    this.configuration = new StoreConfiguration(
      null,
      new StorePaymentConfiguration(null, null, null, false, null, null, null),
      new PosConfiguration(null, null, null, null, null, null, null, null, null, null),
      null,
      null,
      null,
      null,
      null,
      null
    );
    this.metaData = new MetaData(null);
  }

  static fromAdminStore(adminStore: AdminStore): CreateAdminStoreData {
    const createData = new CreateAdminStoreData();
    createData.code = adminStore.code;
    createData.name = adminStore.name;
    createData.address = adminStore.address;
    createData.email = adminStore.email;
    createData.telephone = adminStore.telephone;
    createData.location.latitude = adminStore.location?.latitude ?? null;
    createData.location.longitude = adminStore.location?.longitude ?? null;
    createData.timeZone = adminStore.timeZone;
    createData.vatNumber = adminStore.vatNumber;
    createData.active = adminStore.active ?? true;

    if (adminStore.configuration) {
      createData.configuration = JSON.parse(JSON.stringify(adminStore.configuration));
    }
    if (!createData.configuration.paymentConfiguration) {
      createData.configuration.paymentConfiguration = new StorePaymentConfiguration(
        null,
        null,
        null,
        false,
        null,
        null,
        null
      );
    }
    if (!createData.configuration.posConfiguration) {
      createData.configuration.posConfiguration = new PosConfiguration(
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null
      );
    }
    if (!createData.configuration.clickAndCollectConfiguration) {
      createData.configuration.clickAndCollectConfiguration = new ClickAndCollectConfiguration(false, false);
    }
    if (!createData.configuration.calendarConfiguration) {
      createData.configuration.calendarConfiguration = new CalendarConfiguration(
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null
      );
    }

    if (adminStore.metaData) {
      createData.metaData = JSON.parse(JSON.stringify(adminStore.metaData));
    }
    return createData;
  }

  isBasicInfoValid(): boolean {
    return (
      (this.code?.length ?? 0) > 0 &&
      (this.name?.length ?? 0) > 0 &&
      (this.address?.length ?? 0) > 0 &&
      (this.email?.length ?? 0) > 0 &&
      (this.telephone?.length ?? 0) > 0 &&
      this.location.latitude !== null &&
      this.location.longitude !== null &&
      (this.vatNumber?.length ?? 0) > 0
    );
  }

  isPaymentConfigurationValid(): boolean {
    const config = this.configuration.paymentConfiguration;
    return (
      config !== null &&
      ((config.type === StorePaymentConfigurationType.CHECKOUT &&
        (config.publicKey?.length ?? 0) > 0 &&
        (config.secretKey?.length ?? 0) > 0) ||
        (config.type === StorePaymentConfigurationType.WHENTHEN &&
          (config.flowId?.length ?? 0) > 0 &&
          (config.merchantId?.length ?? 0) > 0) ||
        (config.type === StorePaymentConfigurationType.WORLDPAY && (config.merchantEntity?.length ?? 0) > 0))
    );
  }

  isPosConfigurationValid(): boolean {
    const config = this.configuration.posConfiguration;
    return (
      config !== null &&
      ((config.type === PosConfigurationType.OLD && (config.clickAndCollectPosId?.length ?? 0) > 0) ||
        (config.type === PosConfigurationType.TLM &&
          config.company !== null &&
          config.store !== null &&
          (config.userName?.length ?? 0) > 0 &&
          (config.password?.length ?? 0) > 0 &&
          config.defaultWorkstationId !== null &&
          config.operatorId !== null &&
          (config.orderTTL?.length ?? 0) > 0 &&
          (config.suspendTTL?.length ?? 0) > 0))
    );
  }

  isCalendarConfigurationValid(): boolean {
    const cncConfig = this.configuration.clickAndCollectConfiguration;
    const calendarConfig = this.configuration.calendarConfiguration;
    return (
      cncConfig !== null &&
      (!cncConfig.enableClickAndCollect ||
        (cncConfig.enableClickAndCollect &&
          calendarConfig !== null &&
          calendarConfig.perSlotCount !== null &&
          calendarConfig.serviceId !== null &&
          calendarConfig.businessId !== null &&
          calendarConfig.staffId !== null &&
          calendarConfig.timeZone !== null))
    );
  }
}
